import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';

export interface ContactExchangeRequest {
  id: string;
  match_id: string;
  requester_user_id: string;
  receiver_user_id: string;
  requester_approved: boolean;
  receiver_approved: boolean;
  status: string;
  exchange_data: any;
  created_at: string;
  updated_at: string;
}

export const useContactExchange = (matchId?: string) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Obtener solicitudes de un match
  const { data: requests, isLoading } = useQuery<ContactExchangeRequest[]>({
    queryKey: ['contact-exchange', matchId],
    queryFn: async () => {
      if (!matchId) return [];
      
      const { data, error } = await supabase.functions.invoke('request-contact-exchange', {
        body: { action: 'get', match_id: matchId }
      });

      if (error) throw error;
      return data.requests || [];
    },
    enabled: !!matchId
  });

  // Crear solicitud
  const createRequest = useMutation({
    mutationFn: async (match_id: string) => {
      const { data, error } = await supabase.functions.invoke('request-contact-exchange', {
        body: { action: 'create', match_id }
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast({
        title: t('contactExchange.toast.requestSentTitle'),
        description: t('contactExchange.toast.requestSentDescription')
      });
      queryClient.invalidateQueries({ queryKey: ['contact-exchange'] });
    },
    onError: (error: Error) => {
      toast({
        title: t('contactExchange.toast.failedToSend'),
        description: error.message,
        variant: 'destructive'
      });
    }
  });

  // Aprobar solicitud
  const approveRequest = useMutation({
    mutationFn: async (params: { request_id: string; contact_data: any }) => {
      const { data, error } = await supabase.functions.invoke('request-contact-exchange', {
        body: {
          action: 'approve',
          request_id: params.request_id,
          contact_data: params.contact_data
        }
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast({
        title: t('contactExchange.toast.contactSharedTitle'),
        description: t('contactExchange.toast.contactSharedDescription')
      });
      queryClient.invalidateQueries({ queryKey: ['contact-exchange'] });
    },
    onError: (error: Error) => {
      toast({
        title: t('contactExchange.toast.failedToApprove'),
        description: error.message,
        variant: 'destructive'
      });
    }
  });

  // Rechazar solicitud
  const rejectRequest = useMutation({
    mutationFn: async (request_id: string) => {
      const { data, error } = await supabase.functions.invoke('request-contact-exchange', {
        body: { action: 'reject', request_id }
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast({
        title: t('contactExchange.toast.requestDeclinedTitle'),
        description: t('contactExchange.toast.requestDeclinedDescription')
      });
      queryClient.invalidateQueries({ queryKey: ['contact-exchange'] });
    },
    onError: (error: Error) => {
      toast({
        title: t('contactExchange.toast.failedToDecline'),
        description: error.message,
        variant: 'destructive'
      });
    }
  });

  return {
    requests,
    isLoading,
    createRequest,
    approveRequest,
    rejectRequest
  };
};
